/* ==========================================================================
   Pool Busters — Robotic Cleaner Finder (pb-rc-*)
   One IIFE, one global (PB_RC). Reads the catalogue from the JSON that
   snippets/pb-rc-data.liquid prints: static specs + live Shopify prices.

   The homepage section runs in three stages:
     1. a one-question-at-a-time quiz beside the pool plan,
     2. the top 3 matches,
     3. the Shopify-rendered shop grid (specs added here), with an optional
        side-by-side comparison that opens in place.

   Recommendation rules (kept deliberately simple and auditable):
   1. Hard filters: the pool type/size must be one the manufacturer rates the
      model for, and the model must physically clean every surface asked for.
   2. Rank the survivors by the customer's priority score (0–3, each score
      justified in docs/ROBOTIC-CLEANERS-SPECS.md), then by price.
   3. If nothing survives, relax the surface request one step and say so —
      never silently show a cleaner that can't do what was asked.
   ========================================================================== */
(function () {
  'use strict';
  if (window.PB_RC) return;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var each = function (list, fn) { Array.prototype.forEach.call(list, fn); };

  function readData(root) {
    var el = (root && root.querySelector('script[data-pbrc-data]')) || document.querySelector('script[data-pbrc-data]');
    if (!el) return null;
    try { var d = JSON.parse(el.textContent); } catch (e) { return null; }
    // Join specs to live store data; drop anything the store doesn't sell now.
    d.list = (d.models || []).map(function (m) {
      var live = d.live && d.live[m.handle];
      if (!live || !live.url) return null;
      var o = {}; for (var k in m) o[k] = m[k];
      o.live = live;
      return o;
    }).filter(Boolean);
    return d;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- coverage: a side view of the pool, lit where the robot reaches ----------
     One drawing, used at three sizes: the big plan panel, every card, and the
     answer chips of "What should the robot clean?". */
  function coverage(m) {
    if (m.kind === 'skimmer') return { surface: true };
    return { floor: !!m.clean.floor, walls: !!m.clean.walls, waterline: !!m.clean.waterline, steps: !!m.steps };
  }
  var ZONES = [
    ['floor', 'Floor'], ['walls', 'Walls'], ['waterline', 'Waterline'], ['steps', 'Steps']
  ];
  function sideSVG(cov, cls) {
    var z = function (k) { return cov[k] ? ' is-on' : ''; };
    // Steps are only drawn when they matter (a model that cleans them, or a
    // customer who said their pool has them); otherwise the shallow end is a
    // plain wall, so the drawing stays quiet.
    var steps = cov.steps || cov.stepsShown;
    return '<svg class="pbrc-side ' + (cls || '') + '" viewBox="0 0 200 92" aria-hidden="true" focusable="false">' +
      '<path class="side-deck" d="M0 20H16M184 20H200"/>' +
      '<path class="side-water" d="' + (steps ? 'M16 27H184V80H122L72 60H38V48H27V38H16Z' : 'M16 27H184V80H122L72 60H16Z') + '"/>' +
      '<path class="side-surface' + z('surface') + '" d="M18 27H182"/>' +
      '<path class="side-zone side-walls' + z('walls') + '" d="' + (steps ? 'M16 22V38M184 22V80' : 'M16 22V60M184 22V80') + '"/>' +
      '<path class="side-zone side-floor' + z('floor') + '" d="' + (steps ? 'M38 60H72L122 80H184' : 'M16 60H72L122 80H184') + '"/>' +
      (steps ? '<path class="side-zone side-steps' + z('steps') + '" d="M16 38H27V48H38V60"/>' : '') +
      '<path class="side-zone side-waterline' + z('waterline') + '" d="M16 22V31M184 22V31"/>' +
      '</svg>';
  }
  /* The robot's route in the side view: along the floor, up each wall it can
     climb, a scrub at the waterline, the steps if it cleans them, and back.
     It only ever goes where the cleaner is rated to go. Points sit just
     inside the water so the robot rides on the surface it is cleaning. */
  function sideRoute(cov) {
    var P = [];
    if (cov.surface) return [[24, 24], [176, 24], [24, 24]];
    var steps = cov.steps, top = cov.waterline ? 26 : 38;
    var L = steps ? [44, 54] : [22, 54];
    var scrub = function (x) { return [[x, 31], [x, 26], [x, 31], [x, 26]]; };
    P.push(L, [72, 54], [122, 74], [178, 74]);
    if (cov.walls) { P.push([178, top]); if (cov.waterline) P = P.concat(scrub(178)); P.push([178, 74]); }
    P.push([122, 74], [72, 54], L);
    if (steps) {
      P.push([44, 42], [33, 42], [33, 32], [22, 32]);
      if (cov.walls) { P.push([22, top]); if (cov.waterline) P = P.concat(scrub(22)); P.push([22, 32]); }
      P.push([33, 32], [33, 42], [44, 42], L);
    } else if (cov.walls) {
      P.push([22, top]); if (cov.waterline) P = P.concat(scrub(22)); P.push(L);
    }
    return P;
  }
  function routeOf(pts) {
    var cum = [0];
    for (var i = 1; i < pts.length; i++) cum.push(cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
    return { pts: pts, cum: cum, total: cum[cum.length - 1] };
  }
  var SIDE_ROBOT = '<g class="side-robot" transform="translate(-20 -20)"><rect x="-6" y="-3.5" width="12" height="7" rx="2"/><rect class="side-robot__brush" x="5" y="-3.5" width="2" height="7" rx="1"/></g>';

  function legendHTML(cov) {
    if (cov.surface) return '<ul class="pbrc-cov__legend"><li class="is-on" data-z="surface">Water surface</li><li data-z="floor">Floor</li><li data-z="walls">Walls</li></ul>';
    return '<ul class="pbrc-cov__legend">' + ZONES.map(function (zn) {
      if (zn[0] === 'steps' && !cov.steps && !cov.stepsShown) return '';
      return '<li class="' + (cov[zn[0]] ? 'is-on' : '') + '" data-z="' + zn[0] + '"><span class="pbrc-vh">' + (cov[zn[0]] ? 'Cleans ' : 'Does not clean ') + '</span>' + zn[1] + '</li>';
    }).join('') + '</ul>';
  }
  function coverageHTML(m, compact) {
    var cov = coverage(m);
    return '<div class="pbrc-cov' + (compact ? ' pbrc-cov--sm' : '') + '">' + sideSVG(cov) + legendHTML(cov) + '</div>';
  }

  /* ---------- price + specs ---------- */
  function priceHTML(live, small) {
    var c = 'pbrc-price' + (small ? ' pbrc-price--sm' : '');
    if (live.onSale) {
      return '<div class="' + c + '"><span class="pbrc-price__sale">' + esc(live.money.sale) + '</span>' +
        '<span class="pbrc-price__was"><span class="pbrc-vh">Regular price </span><s>' + esc(live.money.regular) + '</s></span>' +
        '<span class="pbrc-price__save">Save ' + esc(live.money.save) + '</span></div>';
    }
    return '<div class="' + c + '"><span class="pbrc-price__sale">' + esc(live.money.regular) + '</span></div>';
  }
  function imgHTML(live, sizes) {
    if (!live.img) return '';
    return '<img src="' + esc(live.img.s600) + '" srcset="' + esc(live.img.s400) + ' 400w, ' + esc(live.img.s600) + ' 600w, ' + esc(live.img.s800) + ' 800w" sizes="' + sizes + '"' +
      ' width="' + (live.img.w || 600) + '" height="' + (live.img.h || 450) + '" alt="' + esc(live.img.alt) + '" loading="lazy" decoding="async">';
  }
  var SPEC_LABELS = {
    pool: 'Pool size', runtime: 'Runtime', filter: 'Filter', source: 'Power', battery: 'Battery', charge: 'Charging',
    power: 'Suction', nav: 'Navigation', app: 'App', shapes: 'Pool shapes', surfaces: 'Surfaces', access: 'Filter access',
    weight: 'Weight', warranty: 'Warranty', extras: 'Also', picks: 'Our pick for', standout: 'Stands out'
  };
  var KEY_SPECS = ['pool', 'runtime', 'filter'];
  var MORE_SPECS = ['standout', 'picks', 'source', 'battery', 'charge', 'power', 'nav', 'app', 'shapes', 'surfaces', 'access', 'weight', 'warranty'];
  var PICK_NAMES = { fine: 'fine debris', leaves: 'leaves and bigger debris', power: 'strongest overall clean', easy: 'easy ownership', value: 'value' };
  function specValue(m, k) {
    if (k === 'picks') {
      // Pool Busters' own ranking (the 3-of-3 scores), labelled as ours.
      var p = m.score ? Object.keys(PICK_NAMES).filter(function (q) { return m.score[q] === 3; }).map(function (q) { return PICK_NAMES[q]; }) : [];
      return p.length ? p.join(', ').replace(/^./, function (c) { return c.toUpperCase(); }) : '';
    }
    return m.specs && m.specs[k];
  }
  function specRows(m, keys) {
    return keys.map(function (k) {
      var v = specValue(m, k);
      if (v == null || v === '') return '';
      return '<div><dt>' + esc(SPEC_LABELS[k] || k) + '</dt><dd>' + esc(v) + '</dd></div>';
    }).join('');
  }
  function labelsHTML(m) {
    return (m.labels && m.labels.length) ? '<div class="pbrc-labels">' + m.labels.map(function (l) { return '<span class="pbrc-label">' + esc(l) + '</span>'; }).join('') + '</div>' : '';
  }

  /* ---------- stage 2: match card ---------- */
  function cardHTML(m, opts) {
    opts = opts || {};
    var live = m.live;
    var badges = '<div class="pbrc-card__badges">' +
      (live.onSale ? '<span class="pbrc-pill pbrc-pill--sale">' + live.pctOff + '% off</span>' : '<span></span>') +
      (opts.rank ? '<span class="pbrc-pill pbrc-pill--rank">' + esc(opts.rank) + '</span>' : '') + '</div>';
    var more = specRows(m, MORE_SPECS);
    return '<article class="pbrc-card' + (opts.top ? ' pbrc-card--top' : '') + '" data-handle="' + esc(m.handle) + '" data-brand="' + esc(m.brandKey) + '" tabindex="-1">' +
      '<a class="pbrc-card__media" href="' + esc(live.url) + '" tabindex="-1" aria-hidden="true">' + imgHTML(live, '(max-width: 749px) 86vw, 380px') + badges + '</a>' +
      '<div class="pbrc-card__body">' +
        '<div><p class="pbrc-card__brand">' + esc(m.brand) + '</p><h4 class="pbrc-card__name"><a href="' + esc(live.url) + '">' + esc(m.name) + '</a></h4></div>' +
        '<p class="pbrc-card__best"><b>Best for</b> ' + esc(m.bestFor) + '</p>' +
        labelsHTML(m) +
        coverageHTML(m) +
        (opts.why ? '<p class="pbrc-why">' + opts.why + '</p>' : '') +
        // Key specs live inside the toggle here (the shop cards below show them
        // open), which keeps the three match cards short.
        '<details class="pbrc-more"><summary>All specifications</summary><dl class="pbrc-specs">' + specRows(m, KEY_SPECS) + more + '</dl></details>' +
        '<div class="pbrc-buy">' + priceHTML(live) +
        '<a class="pbrc-btn' + (live.available ? '' : ' pbrc-btn--ghost') + '" href="' + esc(live.url) + '">' + (live.available ? 'Shop now<span class="pbrc-vh">: ' + esc(m.name) + '</span>' : 'Sold out') + '</a></div>' +
      '</div></article>';
  }

  /* ---------- recommendation engine ---------- */
  var SURFACE_ORDER = ['floor', 'walls', 'waterline'];
  function cleansAll(m, want) {
    var need = SURFACE_ORDER.slice(0, SURFACE_ORDER.indexOf(want) + 1);
    return need.every(function (s) { return m.clean && m.clean[s]; });
  }
  function rank(list, a) {
    var cleaners = list.filter(function (m) { return m.kind === 'cleaner' && m.live.available; });
    var want = a.clean, relaxed = null, pool;
    function eligible(w) {
      return cleaners.filter(function (m) { return m.sizes.indexOf(a.size) > -1 && cleansAll(m, w); });
    }
    pool = eligible(want);
    while (!pool.length && SURFACE_ORDER.indexOf(want) > 0) {
      want = SURFACE_ORDER[SURFACE_ORDER.indexOf(want) - 1];
      relaxed = want;
      pool = eligible(want);
    }
    var sizeIdx = ['ag', 'sm', 'md', 'lg'];
    pool.forEach(function (m) {
      var s = (m.score && m.score[a.priority]) || 0;
      // Headroom: rated for a size larger than this pool. Counts only for the
      // strongest-clean priority — elsewhere it would push big-pool flagships
      // onto small pools.
      var maxIdx = Math.max.apply(null, m.sizes.map(function (z) { return sizeIdx.indexOf(z); }));
      var head = maxIdx - sizeIdx.indexOf(a.size);
      // Every inground model is rated for freeform and kidney shapes, so shape
      // only matters for steps and sun ledges — which one model cleans.
      var stepsBonus = (a.size !== 'ag' && a.shape === 'steps' && m.steps) ? 15 : 0;
      m._s = s * 10 + stepsBonus + (a.priority === 'power' ? head * 2 : 0);
      m._price = m.live.saleCents || m.live.regularCents || 0;
    });
    // Ties go to the lower price: price is never a proxy for performance.
    pool.sort(function (x, y) { return (y._s - x._s) || (x._price - y._price); });
    return { picks: pool.slice(0, 3), relaxed: relaxed };
  }

  var LABELS = {
    size: { ag: 'above-ground', sm: 'small inground', md: 'medium inground', lg: 'large inground' },
    clean: { floor: 'the floor', walls: 'the floor and walls', waterline: 'the floor, walls and waterline' },
    priority: { fine: 'fine debris', leaves: 'leaves and heavier debris', power: 'the strongest overall clean', easy: 'easy ownership', value: 'value' }
  };

  // Size and surfaces are guaranteed by the filter (and drawn in the coverage
  // diagram), so the line only explains the priority — and steps, if asked.
  function whyLine(m, a) {
    var r = (m.why && m.why[a.priority]) || '';
    var st = (a.size !== 'ag' && a.shape === 'steps' && m.why && m.why.steps) ? m.why.steps : '';
    var txt = [st, r].filter(Boolean).join(' ');
    return txt ? '<b>Why it fits:</b> ' + esc(txt) : '';
  }

  /* ---------- pool plan (top view + side view) ---------- */
  var SCALE = { ag: 1, sm: 0.8, md: 0.9, lg: 1 };
  function lanePath(shapeEl, inset, step) {
    // Scan the real shape so the robot's lanes stay inside whatever outline
    // is drawn. Falls back to the bounding box if isPointInFill is missing.
    var b = shapeEl.getBBox(), d = '', dir = 0, x, y, top, bot, prev = null;
    var canTest = typeof shapeEl.isPointInFill === 'function' && typeof DOMPoint === 'function';
    function inside(px, py) {
      if (!canTest) return true;
      try { return shapeEl.isPointInFill(new DOMPoint(px, py)); } catch (e) { return true; }
    }
    function inset4(px, py) { return inside(px, py) && inside(px - inset, py) && inside(px + inset, py) && inside(px, py - inset) && inside(px, py + inset); }
    for (x = b.x + inset + 6; x <= b.x + b.width - inset - 6; x += step) {
      top = null; bot = null;
      for (y = b.y; y <= b.y + b.height; y += 3) { if (inset4(x, y)) { if (top === null) top = y; bot = y; } }
      if (top === null || bot - top < 20) continue;
      var a1 = dir ? bot : top, a2 = dir ? top : bot;
      if (prev === null) d += 'M' + x.toFixed(1) + ' ' + a1.toFixed(1);
      else {
        // U-turn: bulge toward the wall the robot just reached.
        var ctrl = dir ? Math.max(prev.y, a1) + 12 : Math.min(prev.y, a1) - 12;
        d += ' C' + prev.x.toFixed(1) + ' ' + ctrl.toFixed(1) + ' ' + x.toFixed(1) + ' ' + ctrl.toFixed(1) + ' ' + x.toFixed(1) + ' ' + a1.toFixed(1);
      }
      d += ' L' + x.toFixed(1) + ' ' + a2.toFixed(1);
      prev = { x: x, y: a2 }; dir = 1 - dir;
    }
    return d;
  }

  function Plan(el) {
    this.el = el;
    this.trail = el.querySelector('.pool-trail');
    this.lanes = el.querySelector('.pool-lanes');
    this.robot = el.querySelector('.pool-robot');
    this.scaleG = el.querySelector('.pbrc-plan__scale');
    this.dim = el.querySelector('[data-pbrc-dim]');
    this.clip = el.querySelector('[data-pbrc-clip]');
    this.side = el.querySelector('[data-pbrc-side]');
    this.view = 'top'; this.userView = false;
    var selfV = this;
    each(el.querySelectorAll('[data-pbrc-view]'), function (b) {
      b.addEventListener('click', function () { selfV.userView = true; selfV.setView(b.getAttribute('data-pbrc-view')); });
    });
    this.sideLabel = el.querySelector('[data-pbrc-sidelabel]');
    this.cache = {};
    this.asked = null;
    this.t = 0; this.len = 0; this.st = 0; this.route = null; this.sideRobot = null; this.visible = false; this.raf = 0; this.last = 0;
    var self = this;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        self.visible = es[0].isIntersecting;
        if (self.visible) self.loop(); else cancelAnimationFrame(self.raf);
      }, { threshold: 0.15 }).observe(el);
    } else { this.visible = true; this.loop(); }
  }
  Plan.prototype.set = function (a) {
    var shapeKey = a.size === 'ag' ? 'round' : a.shape;
    var cur = null;
    each(this.el.querySelectorAll('[data-shape]'), function (s) {
      var on = s.getAttribute('data-shape') === shapeKey;
      s.classList.toggle('is-on', on);
      if (on) cur = s;
    });
    if (!cur) return;
    var outline = cur.querySelector('.pool-water');
    if (this.clip) this.clip.setAttribute('d', outline.getAttribute('d'));
    if (this.scaleG) this.scaleG.style.transform = 'scale(' + (SCALE[a.size] || 1) + ')';
    if (this.dim) this.dim.textContent = this.el.getAttribute('data-dim-' + a.size) || '';
    this.asked = { floor: true, walls: a.clean !== 'floor', waterline: a.clean === 'waterline', steps: a.size !== 'ag' && a.shape === 'steps' };
    this.show(null);
    this.outline = outline; this.shapeKey = shapeKey;
    this.layoutLanes();
  };
  // Lanes are measured from the drawn outline, which can't be measured while
  // the top view is hidden — so layout waits until it is showing.
  Plan.prototype.layoutLanes = function () {
    if (this.view !== 'top') { this.lanesDirty = true; return; }
    this.lanesDirty = false;
    var shapeKey = this.shapeKey, outline = this.outline;
    if (!outline) return;
    if (!this.cache[shapeKey]) {
      var lp = lanePath(outline, 26, 34);
      if (!lp) return;
      this.cache[shapeKey] = lp;
    }
    var d = this.cache[shapeKey];
    if (this.trail.getAttribute('d') !== d) {
      this.lanes.setAttribute('d', d);
      this.trail.setAttribute('d', d);
      this.len = this.trail.getTotalLength ? this.trail.getTotalLength() : 0;
      this.trail.style.strokeDasharray = this.len + ' ' + this.len;
      this.t = reduceMotion ? this.len : 0;
      this.draw();
      if (this.visible) this.loop();
    }
  };
  // Light the zones for one cleaner (hover/focus on its card), or — with no
  // model — for what the customer asked for.
  Plan.prototype.show = function (m, preview) {
    var cov = m ? coverage(m) : (preview || this.asked);
    if (!cov) return;
    // Customer has steps: keep them in the drawing so a model that can't clean
    // them visibly leaves them grey.
    if (m && this.asked && this.asked.steps && !cov.surface) { cov = Object.assign({}, cov, { stepsShown: true }); }
    this.el.setAttribute('data-walls', cov.walls ? '1' : '0');
    this.el.setAttribute('data-waterline', cov.waterline ? '1' : '0');
    this.el.classList.toggle('is-model', !!m);
    if (this.side) {
      this.side.innerHTML = sideSVG(cov, 'pbrc-side--lg') + legendHTML(cov);
      // The robot works the route for this coverage (not under reduced motion,
      // where the lit zones alone carry the meaning).
      var svg = this.side.querySelector('svg');
      if (svg && !reduceMotion) {
        svg.insertAdjacentHTML('beforeend', SIDE_ROBOT);
        this.sideRobot = svg.querySelector('.side-robot');
        this.route = routeOf(sideRoute(cov));
        this.st = 0;
        this.drawSide();
        if (this.visible) this.loop();
      }
    }
    if (this.sideLabel) this.sideLabel.textContent = m ? m.name + ' reaches' : (preview ? 'This option cleans' : 'You asked for');
  };
  Plan.prototype.draw = function () {
    if (!this.len) return;
    var t = Math.min(this.t, this.len);
    this.trail.style.strokeDashoffset = (this.len - t).toFixed(1);
    var p = this.trail.getPointAtLength(t), q = this.trail.getPointAtLength(Math.min(t + 2, this.len));
    var ang = Math.atan2(q.y - p.y, q.x - p.x) * 180 / Math.PI;
    this.robot.setAttribute('transform', 'translate(' + p.x.toFixed(1) + ' ' + p.y.toFixed(1) + ') rotate(' + ang.toFixed(1) + ')');
  };
  Plan.prototype.setView = function (v) {
    if (v === this.view) return;
    this.view = v;
    this.el.setAttribute('data-view', v);
    each(this.el.querySelectorAll('[data-pbrc-view]'), function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-pbrc-view') === v ? 'true' : 'false'); });
    if (v === 'top' && this.lanesDirty) this.layoutLanes();
    if (this.visible) this.loop();
  };
  Plan.prototype.drawSide = function () {
    var r = this.route;
    if (!r || !this.sideRobot || !r.total) return;
    var d = this.st % r.total, i = 1;
    while (i < r.cum.length - 1 && r.cum[i] < d) i++;
    var a = r.pts[i - 1], b = r.pts[i], seg = (r.cum[i] - r.cum[i - 1]) || 1, k = (d - r.cum[i - 1]) / seg;
    var x = a[0] + (b[0] - a[0]) * k, y = a[1] + (b[1] - a[1]) * k;
    var ang = Math.atan2(b[1] - a[1], b[0] - a[0]) * 180 / Math.PI;
    // Scrubbing at the waterline: keep the robot upright against the wall
    // rather than flipping as it nudges up and down.
    if (Math.abs(b[0] - a[0]) < .01 && Math.abs(b[1] - a[1]) < 6) ang = -90;
    this.sideRobot.setAttribute('transform', 'translate(' + x.toFixed(1) + ' ' + y.toFixed(1) + ') rotate(' + ang.toFixed(0) + ')');
  };
  Plan.prototype.loop = function () {
    var self = this;
    cancelAnimationFrame(this.raf);
    if (reduceMotion || (!this.len && !this.route)) { this.t = this.len; this.draw(); return; }
    this.last = 0;
    function tick(ts) {
      if (!self.visible) return;
      var dt = self.last ? Math.min(ts - self.last, 64) : 16; self.last = ts;
      self.t += dt * 0.11; // px per ms — a slow, steady lap
      if (self.t > self.len + 260) self.t = 0; // brief rest at the end, then a fresh lap
      // Only the view on screen is drawn.
      if (self.view === 'top') self.draw();
      else { self.st += dt * 0.04; self.drawSide(); }
      self.raf = requestAnimationFrame(tick);
    }
    this.raf = requestAnimationFrame(tick);
  };

  /* ---------- finder ---------- */
  function Finder(root) {
    this.root = root;
    this.data = readData(root);
    if (!this.data || !this.data.list.length) return;
    this.byHandle = {};
    var self = this;
    this.data.list.forEach(function (m) { self.byHandle[m.handle] = m; });
    this.form = root.querySelector('[data-pbrc-form]');
    this.cards = root.querySelector('[data-pbrc-cards]');
    this.sum = root.querySelector('[data-pbrc-sum]');
    this.addon = root.querySelector('[data-pbrc-addon]');
    this.swipe = root.querySelector('[data-pbrc-swipe]');
    this.results = root.querySelector('[data-pbrc-results]');
    this.plan = root.querySelector('[data-pbrc-plan]') ? new Plan(root.querySelector('[data-pbrc-plan]')) : null;
    this.done = false;
    this.form.addEventListener('change', function () { self.update(true); });
    this.form.addEventListener('submit', function (e) { e.preventDefault(); });
    this.setupQuiz();
    this.setupGlyphs();
    this.setupSwipe();
    this.setupSticky();
    this.setupShop();
    this.setupCompare();
    this.setupHover();
    this.update(false);
  }
  Finder.prototype.answers = function () {
    var f = this.form, get = function (n) { var c = f.querySelector('input[name="' + n + '"]:checked'); return c ? c.value : null; };
    return { size: get('pbrc-size') || 'md', shape: get('pbrc-shape') || 'rect', clean: get('pbrc-clean') || 'waterline', priority: get('pbrc-priority') || 'power' };
  };
  Finder.prototype.update = function (animate) {
    var a = this.answers(), self = this;
    var shapeQ = this.root.querySelector('[data-pbrc-q="shape"]');
    if (shapeQ) shapeQ.hidden = a.size === 'ag'; // above-ground: shape doesn't change the pick
    this.renderProgress();
    if (this.plan) this.plan.set(a);
    var r = rank(this.data.list, a);
    this.picks = r.picks;
    var html;
    if (!r.picks.length) {
      html = '<p class="pbrc-empty">None of the cleaners we carry is rated for that combination. Call Pool Busters at ' + esc(this.data.phone || '289-929-7665') + ' and we’ll find the right fit.</p>';
    } else {
      html = r.picks.map(function (m, i) {
        return cardHTML(m, { rank: i === 0 ? 'Best match' : null, top: i === 0, why: whyLine(m, a) });
      }).join('');
    }
    var sum = 'For ' + (a.size === 'ag' ? 'an ' : 'a ') + LABELS.size[a.size] + ' pool' + (a.size !== 'ag' ? ' (' + this.shapeLabel(a.shape) + ')' : '') + ', cleaning ' + LABELS.clean[a.clean] + ', with priority on ' + LABELS.priority[a.priority] + '.';
    if (r.relaxed) sum += ' None of our ' + (a.size === 'ag' ? 'above-ground ' : '') + 'cleaners is rated to clean ' + (a.clean === 'waterline' ? 'the waterline' : 'walls') + ' in this pool size, so these are the closest fits.';
    if (!this.done) sum += ' Answer the questions above to fine-tune these.';
    var apply = function () {
      self.cards.innerHTML = html;
      self.cards.setAttribute('data-count', String(Math.max(r.picks.length, 1)));
      if (self.sum) self.sum.textContent = sum;
      self.renderAddon(a, r.picks[0]);
      self.renderSwipeDots(r.picks.length);
      if (self.cards.scrollLeft) self.cards.scrollLeft = 0;
      self.markMatches(r.picks);
      self.renderSummary(a);
    };
    if (animate && !reduceMotion) {
      this.cards.classList.add('is-swapping');
      clearTimeout(this._sw);
      this._sw = setTimeout(function () { apply(); requestAnimationFrame(function () { self.cards.classList.remove('is-swapping'); }); }, 200);
    } else apply();
  };
  Finder.prototype.shapeLabel = function (s) {
    var el = this.form.querySelector('input[name="pbrc-shape"][value="' + s + '"]');
    return el ? el.getAttribute('data-short') : s;
  };
  Finder.prototype.renderAddon = function (a, top) {
    if (!this.addon) return;
    var skimmers = this.data.list.filter(function (m) { return m.kind === 'skimmer' && m.live.available; });
    if (a.priority !== 'leaves' || !skimmers.length) { this.addon.hidden = true; return; }
    var s = (top && skimmers.filter(function (m) { return m.brandKey === top.brandKey; })[0]) || skimmers[0];
    var live = s.live;
    this.addon.innerHTML = (live.img ? '<img src="' + esc(live.img.s400) + '" width="76" height="76" alt="" loading="lazy">' : '<span></span>') +
      '<p class="pbrc-addon__t"><b>Trees around the pool?</b> A robotic skimmer works the surface, catching leaves before they sink. ' +
      'Pair the ' + esc(s.name) + ' with your cleaner. <span class="pbrc-addon__p">' + (live.onSale ? '<s>' + esc(live.money.regular) + '</s>' + esc(live.money.sale) : esc(live.money.regular)) + '</span></p>' +
      '<a class="pbrc-btn pbrc-btn--ghost" href="' + esc(live.url) + '">See the skimmer</a>';
    this.addon.hidden = false;
  };

  /* stage 1: one question at a time, every screen size */
  Finder.prototype.setupQuiz = function () {
    var self = this;
    this.qEls = Array.prototype.slice.call(this.root.querySelectorAll('.pbrc-q'));
    this.step = 0;
    this.count = this.root.querySelector('[data-pbrc-count]');
    this.fill = this.root.querySelector('[data-pbrc-fill]');
    this.back = this.root.querySelector('[data-pbrc-back]');
    this.next = this.root.querySelector('[data-pbrc-next]');
    this.doneEl = this.root.querySelector('[data-pbrc-done]');
    this.quizEl = this.root.querySelector('[data-pbrc-quiz]');
    if (this.back) this.back.addEventListener('click', function () { self.go(self.step - 1, -1); });
    if (this.next) this.next.addEventListener('click', function () { self.advance(); });
    // A pointer click on an answer — even on the one already selected — moves
    // on. Keyboard users move with arrows and press Next, so arrowing through
    // the options never jumps them to the next question.
    this.form.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || t.type !== 'radio' || e.detail === 0) return;
      clearTimeout(self._adv);
      self._adv = setTimeout(function () { self.advance(); }, reduceMotion ? 0 : 320);
    });
    if (this.doneEl) this.doneEl.addEventListener('click', function (e) {
      var b = e.target.closest('[data-pbrc-edit]');
      if (!b) return;
      var q = b.getAttribute('data-pbrc-edit');
      self.done = false;
      self.quizEl.classList.remove('is-done');
      var idx = self.visibleQs().map(function (x) { return x.getAttribute('data-pbrc-q'); }).indexOf(q);
      self.go(idx < 0 ? 0 : idx, -1);
      self.update(false);
      var cur = self.visibleQs()[self.step];
      var inp = cur && (cur.querySelector('input:checked') || cur.querySelector('input'));
      if (inp) inp.focus({ preventScroll: true });
    });
    this.go(0, 0);
  };
  Finder.prototype.visibleQs = function () { return this.qEls.filter(function (q) { return !q.hidden; }); };
  Finder.prototype.go = function (i, dir) {
    var vis = this.visibleQs();
    this.step = Math.max(0, Math.min(i, vis.length - 1));
    this.qEls.forEach(function (q) { q.classList.remove('is-current', 'from-back', 'from-next'); });
    var cur = vis[this.step];
    if (cur && this.plan && !this.plan.userView && cur.getAttribute('data-pbrc-q') === 'clean') this.plan.setView('side');
    if (cur) {
      cur.classList.add('is-current');
      if (dir && !reduceMotion) cur.classList.add(dir < 0 ? 'from-back' : 'from-next');
    }
    this.renderProgress();
  };
  Finder.prototype.renderProgress = function () {
    if (!this.qEls) return;
    var vis = this.visibleQs(), n = vis.length;
    var at = this.done ? n : this.step + 1;
    if (this.count) this.count.textContent = this.done ? 'All done' : 'Question ' + at + ' of ' + n;
    if (this.fill) this.fill.style.transform = 'scaleX(' + (this.done ? 1 : (this.step + 1) / (n + 1)) + ')';
    if (this.back) this.back.disabled = this.step === 0 && !this.done;
    if (this.next) this.next.textContent = this.step >= n - 1 ? 'See my matches' : 'Next';
  };
  Finder.prototype.advance = function () {
    var vis = this.visibleQs();
    if (this.step < vis.length - 1) { this.go(this.step + 1, 1); return; }
    this.finish();
  };
  Finder.prototype.finish = function () {
    this.done = true;
    if (this.quizEl) this.quizEl.classList.add('is-done');
    this.update(false);
    this.renderProgress();
    this.toResults();
  };
  Finder.prototype.renderSummary = function (a) {
    if (!this.doneEl) return;
    var f = this.form, label = function (name) {
      var c = f.querySelector('input[name="' + name + '"]:checked');
      return c ? (c.getAttribute('data-chip') || c.value) : '';
    };
    var chips = [['size', label('pbrc-size')]];
    if (a.size !== 'ag') chips.push(['shape', label('pbrc-shape')]);
    chips.push(['clean', label('pbrc-clean')], ['priority', label('pbrc-priority')]);
    this.doneEl.querySelector('[data-pbrc-chips]').innerHTML = chips.map(function (c) {
      return '<button type="button" class="pbrc-chip" data-pbrc-edit="' + c[0] + '"><span class="pbrc-vh">Change: </span>' + esc(c[1]) + '</button>';
    }).join('');
  };
  Finder.prototype.toResults = function () {
    if (!this.results) return;
    this.results.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  };

  // Tiny side-view glyphs inside the "What should the robot clean?" answers,
  // and a preview of each option on the pool plan while it is hovered.
  Finder.prototype.setupGlyphs = function () {
    var self = this;
    each(this.root.querySelectorAll('[data-pbrc-glyph]'), function (g) {
      var k = g.getAttribute('data-pbrc-glyph');
      g.innerHTML = sideSVG({ floor: true, walls: k !== 'floor', waterline: k === 'waterline' }, 'pbrc-side--xs');
      var label = g.closest('label');
      if (!label || !self.plan) return;
      var prev = { floor: true, walls: k !== 'floor', waterline: k === 'waterline' };
      label.addEventListener('mouseenter', function () { self.plan.show(null, prev); });
      label.addEventListener('mouseleave', function () { self.plan.show(null); });
    });
  };

  // Hover or focus any cleaner card (matches or shop) → the plan shows what
  // that cleaner reaches. Leaving returns it to what the customer asked for.
  Finder.prototype.setupHover = function () {
    var self = this;
    if (!this.plan) return;
    var on = function (e) {
      var c = e.target.closest && e.target.closest('[data-handle]');
      var m = c && self.byHandle[c.getAttribute('data-handle')];
      if (m) self.plan.show(m);
    };
    var off = function (e) {
      var c = e.target.closest && e.target.closest('[data-handle]');
      if (c && !(e.relatedTarget && c.contains(e.relatedTarget))) self.plan.show(null);
    };
    this.root.addEventListener('mouseover', on);
    this.root.addEventListener('focusin', on);
    this.root.addEventListener('mouseout', off);
    this.root.addEventListener('focusout', off);
  };

  /* stage 3: the Shopify-rendered shop grid, with specs added from the data */
  Finder.prototype.setupShop = function () {
    var self = this, shop = this.root.querySelector('[data-pbrc-shop]');
    if (!shop) return;
    this.shopCards = Array.prototype.slice.call(shop.querySelectorAll('.pbrc-shop-card[data-handle]'));
    this.shopCards.forEach(function (c) {
      var m = self.byHandle[c.getAttribute('data-handle')];
      if (!m) return;
      // The clean model name reads faster than the full store title, which
      // stays on the product page and in the image alt text.
      var ttl = c.querySelector('.pbrc-shop-card__title a');
      if (ttl) { ttl.setAttribute('title', ttl.textContent); ttl.textContent = m.name; }
      var lb = c.querySelector('[data-pbrc-labels]');
      if (lb) lb.innerHTML = labelsHTML(m);
      var sp = c.querySelector('[data-pbrc-specs]');
      if (sp) {
        var more = specRows(m, MORE_SPECS);
        // Three key figures at a glance; everything else one tap away.
        var keys = (m.keys || []).map(function (k) { return '<div><dt>' + esc(k[0]) + '</dt><dd>' + esc(k[1]) + '</dd></div>'; }).join('');
        sp.innerHTML = coverageHTML(m, true) + (keys ? '<dl class="pbrc-keys">' + keys + '</dl>' : '') +
          '<details class="pbrc-more pbrc-more--sm"><summary>Full specs</summary><dl class="pbrc-specs pbrc-specs--sm">' + specRows(m, KEY_SPECS) + more + '</dl></details>';
      }
    });
    var grid = shop.querySelector('.pbrc-shop__grid');
    each(shop.querySelectorAll('[data-pbrc-scroll]'), function (b) {
      b.addEventListener('click', function () {
        var c = self.shopCards.filter(function (x) { return !x.hidden; })[0];
        var step = c ? c.getBoundingClientRect().width + 16 : 300;
        grid.scrollBy({ left: step * Number(b.getAttribute('data-pbrc-scroll')) * (window.innerWidth > 1100 ? 2 : 1), behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    });
    var syncArrows = function () {
      var prev = shop.querySelector('[data-pbrc-scroll="-1"]'), next = shop.querySelector('[data-pbrc-scroll="1"]');
      if (!prev || !next) return;
      prev.disabled = grid.scrollLeft < 4;
      next.disabled = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 4;
    };
    grid.addEventListener('scroll', function () { clearTimeout(self._ar); self._ar = setTimeout(syncArrows, 60); }, { passive: true });
    window.addEventListener('resize', syncArrows);
    setTimeout(syncArrows, 0);
    this.syncArrows = syncArrows;
    var btns = shop.querySelectorAll('[data-pbrc-shopbrand]'), cards = this.shopCards;
    each(btns, function (b) {
      var key = b.getAttribute('data-pbrc-shopbrand');
      if (key !== 'all' && !cards.some(function (c) { return c.getAttribute('data-brand') === key; })) b.hidden = true;
      b.addEventListener('click', function () {
        each(btns, function (o) { o.setAttribute('aria-pressed', o === b ? 'true' : 'false'); });
        self.brand = key;
        cards.forEach(function (c) { c.hidden = key !== 'all' && c.getAttribute('data-brand') !== key; });
        grid.scrollLeft = 0; syncArrows();
        self.filterTable();
      });
    });
  };
  Finder.prototype.markMatches = function (picks) {
    var hs = picks.map(function (m) { return m.handle; });
    (this.shopCards || []).forEach(function (c) {
      var on = hs.indexOf(c.getAttribute('data-handle')) > -1;
      c.classList.toggle('is-match', on);
      var tag = c.querySelector('[data-pbrc-matchtag]');
      if (tag) tag.hidden = !on;
    });
    if (this.table) each(this.table.querySelectorAll('[data-h]'), function (c) { c.classList.toggle('is-match', hs.indexOf(c.getAttribute('data-h')) > -1); });
  };

  /* optional: side-by-side comparison, opened in place */
  Finder.prototype.setupCompare = function () {
    var self = this;
    this.cmpBtn = this.root.querySelector('[data-pbrc-cmp-toggle]');
    this.cmpPanel = this.root.querySelector('[data-pbrc-cmp]');
    if (!this.cmpBtn || !this.cmpPanel) return;
    this.table = this.cmpPanel.querySelector('[data-pbrc-table]');
    var list = this.data.list.filter(function (m) { return m.kind === 'cleaner'; }).sort(function (a, b) { return (a.order || 99) - (b.order || 99); });
    this.table.innerHTML = tableHTML(list);
    this.cmpBtn.addEventListener('click', function () { self.toggleCompare(); });
    each(this.root.querySelectorAll('[data-pbrc-open-cmp]'), function (a) {
      a.addEventListener('click', function (e) { e.preventDefault(); self.toggleCompare(true); });
    });
    if (location.hash === '#compare-cleaners') setTimeout(function () { self.toggleCompare(true); }, 300);
  };
  Finder.prototype.toggleCompare = function (force) {
    var open = force === true ? true : this.cmpBtn.getAttribute('aria-expanded') !== 'true';
    this.cmpBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.cmpPanel.classList.toggle('is-open', open);
    this.cmpPanel.setAttribute('aria-hidden', open ? 'false' : 'true');
    if ('inert' in this.cmpPanel) this.cmpPanel.inert = !open;
    var lbl = this.cmpBtn.querySelector('[data-pbrc-cmp-label]');
    if (lbl) lbl.textContent = open ? 'Hide the comparison' : 'Compare cleaners side by side';
    if (open && force === true) this.cmpBtn.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  };
  Finder.prototype.filterTable = function () {
    if (!this.table) return;
    var key = this.brand || 'all';
    each(this.table.querySelectorAll('[data-brand]'), function (c) { c.hidden = key !== 'all' && c.getAttribute('data-brand') !== key; });
  };
  function tableHTML(list) {
    function s(k) { return function (m) { var v = specValue(m, k); return esc(v || '—'); }; }
    function yn(k) { return function (m) { return k === 'steps' ? (m.steps ? 'Yes' : 'No') : (m.clean[k] ? 'Yes' : 'No'); }; }
    var rows = [
      ['Price', function (m) { return m.live.onSale ? '<b>' + esc(m.live.money.sale) + '</b> <s>' + esc(m.live.money.regular) + '</s>' : '<b>' + esc(m.live.money.regular) + '</b>'; }],
      ['Coverage', function (m) { return sideSVG(coverage(m), 'pbrc-side--table'); }],
      ['Floor', yn('floor')], ['Walls', yn('walls')], ['Waterline', yn('waterline')], ['Steps', yn('steps')],
      ['Pool size', s('pool')], ['Pool shapes', s('shapes')], ['Surfaces', s('surfaces')],
      ['Power', s('source')], ['Runtime', s('runtime')], ['Battery', s('battery')], ['Charging', s('charge')],
      ['Suction', s('power')], ['Filter', s('filter')], ['Filter access', s('access')], ['Navigation', s('nav')],
      ['App', s('app')], ['Weight', s('weight')], ['Warranty', s('warranty')], ['Our pick for', s('picks')], ['Stands out', s('standout')]
    ];
    var head = '<thead><tr><th scope="col"><span class="pbrc-vh">Specification</span></th>' + list.map(function (m) {
      return '<th scope="col" data-brand="' + esc(m.brandKey) + '" data-h="' + esc(m.handle) + '"><span class="pbrc-cmp__brand">' + esc(m.brand) + '</span><a href="' + esc(m.live.url) + '">' + esc(m.name) + '</a><span class="pbrc-cmp__match">Your match</span></th>';
    }).join('') + '</tr></thead>';
    var body = '<tbody>' + rows.map(function (r) {
      var yesNo = ['Floor', 'Walls', 'Waterline', 'Steps'].indexOf(r[0]) > -1;
      return '<tr><th scope="row">' + r[0] + '</th>' + list.map(function (m) {
        var v = r[1](m), cls = yesNo ? (v === 'Yes' ? ' is-yes' : ' is-no') : '';
        return '<td class="' + cls.trim() + '" data-brand="' + esc(m.brandKey) + '" data-h="' + esc(m.handle) + '">' + v + '</td>';
      }).join('') + '</tr>';
    }).join('') + '</tbody>';
    return '<table>' + head + body + '</table>';
  }

  /* phones: swipe dots for the match cards */
  Finder.prototype.setupSwipe = function () {
    var self = this;
    if (!this.swipe) return;
    this.cards.addEventListener('scroll', function () {
      clearTimeout(self._sc);
      self._sc = setTimeout(function () {
        var cs = self.cards.children; if (!cs.length) return;
        var w = cs[0].getBoundingClientRect().width + 12;
        var idx = Math.round(self.cards.scrollLeft / w);
        each(self.swipe.children, function (d, i) { d.classList.toggle('is-on', i === idx); });
      }, 60);
    }, { passive: true });
  };
  Finder.prototype.renderSwipeDots = function (n) {
    if (!this.swipe) return;
    var h = '';
    for (var i = 0; i < n; i++) h += '<i class="' + (i === 0 ? 'is-on' : '') + '"></i>';
    this.swipe.innerHTML = n > 1 ? h : '';
  };

  /* phones: sticky sale bar. Debut transforms its page container, which turns
     position:fixed into "fixed to that container" — so the bar is moved to
     <body>, carrying the .pbrc wrapper class its colours depend on. */
  Finder.prototype.setupSticky = function () {
    var bar = this.root.querySelector('[data-pbrc-sticky]');
    if (!bar || !('IntersectionObserver' in window)) return;
    // Theme editor re-renders sections: drop the bar a previous render lifted.
    var id = bar.getAttribute('data-pbrc-sticky');
    each(document.querySelectorAll('body > [data-pbrc-sticky="' + id + '"]'), function (old) { old.parentNode.removeChild(old); });
    document.body.appendChild(bar);
    var inSection = false, seeing = false;
    var sync = function () { bar.classList.toggle('is-on', inSection && !seeing); bar.setAttribute('aria-hidden', inSection && !seeing ? 'false' : 'true'); };
    new IntersectionObserver(function (es) { inSection = es[0].isIntersecting; sync(); }, { threshold: 0 }).observe(this.root);
    // Hidden while the matches or the shop grid are on screen — the prices are already in view.
    var shown = new Map();
    var watch = function (el) {
      if (!el) return;
      new IntersectionObserver(function (es) {
        shown.set(el, es[0].isIntersecting);
        seeing = Array.from(shown.values()).some(Boolean); sync();
      }, { threshold: 0.12 }).observe(el);
    };
    watch(this.results);
    watch(this.root.querySelector('[data-pbrc-shop]'));
    var self = this;
    var link = bar.querySelector('a');
    if (link) link.addEventListener('click', function (e) { e.preventDefault(); self.toResults(); });
  };

  function init() {
    each(document.querySelectorAll('[data-pbrc-finder]:not([data-pbrc-ready])'), function (el) { el.setAttribute('data-pbrc-ready', ''); new Finder(el); });
  }
  window.PB_RC = { init: init, _rank: rank };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  // Theme editor: re-run when the section is re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
